/**
 *  @class
 *  @function FreeShipping
 */

if (!customElements.get('free-shipping')) {
  // Static flag to ensure only one instance processes free gift at a time
  let globalFreeGiftProcessing = false;

  class FreeShipping extends HTMLElement {
    constructor() {
      super();
      this.isProcessing = false;
      this.boundHandleCartChange = this.handleCartChange.bind(this);
    }

    connectedCallback() {
      this.update();

      // Listen for all cart-related events globally
      const events = [
        'line-item:change:end', // Cart quantity changes
        'cart:updated', // General cart updates
        'cart:refresh', // Cart refresh
        'product:added-to-cart', // Product added from product page
        'cart:item-added', // Item added to cart
      ];

      events.forEach(event => {
        document.addEventListener(event, this.boundHandleCartChange);
      });
    }

    async handleCartChange(event) {
      if (this.isProcessing || globalFreeGiftProcessing) {
        return;
      }

      // Set processing flags immediately to prevent duplicate calls
      this.isProcessing = true;
      globalFreeGiftProcessing = true;

      try {
        // Minimal delay for cart state to settle
        await new Promise(resolve => setTimeout(resolve, 50));

        // Fetch latest cart and update
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.dataset.cartTotal = cart.total_price;
        this.update();

        // Handle free gift logic (only if this is the first component instance)
        await this.checkAndUpdateFreeGift(cart);
      } catch (error) {
        console.error('Error handling cart change:', error);
      } finally {
        // Minimal delay to prevent rapid re-triggers
        await new Promise(resolve => setTimeout(resolve, 100));
        this.isProcessing = false;
        globalFreeGiftProcessing = false;
      }
    }

    update() {
      const total = parseInt(this.dataset.cartTotal, 10);
      const freeGiftThreshold =
        parseInt(this.dataset.freeGiftThreshold, 10) || 0;
      const shippingMinimum =
        Math.round(
          parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1)
        ) || 0;

      // Collect active milestones (threshold > 0)
      const giftMilestone = this.querySelector(
        '.free-shipping--milestone-gift'
      );
      const shippingMilestone = this.querySelector(
        '.free-shipping--milestone-shipping'
      );

      const milestones = [];
      if (shippingMinimum > 0 && shippingMilestone) {
        milestones.push({
          element: shippingMilestone,
          threshold: shippingMinimum,
          type: 'shipping',
        });
        // Update shipping milestone label with converted price
        this.updateMilestoneLabel(
          shippingMilestone,
          shippingMinimum,
          'Free Shipping'
        );
      }
      if (freeGiftThreshold > 0 && giftMilestone) {
        // Convert free gift threshold using currency rate
        const convertedGiftThreshold = Math.round(
          freeGiftThreshold * (Shopify.currency.rate || 1)
        );
        milestones.push({
          element: giftMilestone,
          threshold: convertedGiftThreshold,
          type: 'gift',
        });
        // Update gift milestone label with converted price
        this.updateMilestoneLabel(
          giftMilestone,
          convertedGiftThreshold,
          'Free Gift'
        );
      }

      // Exit early if no milestones
      if (milestones.length === 0) {
        return;
      }

      // Sort by threshold value (lowest first)
      milestones.sort((a, b) => a.threshold - b.threshold);

      // Update the title text based on achieved milestones
      this.updateTitleText(total, milestones, shippingMinimum);

      // Calculate progress based on visual milestone positions
      let percentage = 0;
      const totalMilestones = milestones.length;

      // Find which milestone range we're in and calculate proportional progress
      for (let i = 0; i < milestones.length; i++) {
        if (total >= milestones[i].threshold) {
          // We've passed this milestone
          percentage = (i + 1) / totalMilestones;
        } else {
          // We're between this milestone and the previous one
          const prevThreshold = i > 0 ? milestones[i - 1].threshold : 0;
          const prevPosition = i > 0 ? i / totalMilestones : 0;
          const currentPosition = (i + 1) / totalMilestones;

          // Calculate proportional progress between milestones
          const progressInRange =
            (total - prevThreshold) / (milestones[i].threshold - prevThreshold);
          const visualRange = currentPosition - prevPosition;

          percentage = prevPosition + progressInRange * visualRange;
          break;
        }
      }

      // Cap at 100%
      percentage = Math.min(percentage, 1);

      // Update progress bar
      this.style.setProperty('--percentage', percentage);

      // Position milestones evenly and highlight if achieved
      milestones.forEach((milestone, index) => {
        if (milestone.element) {
          // Calculate even spacing
          const position = ((index + 1) / totalMilestones) * 100;
          milestone.element.style.left = `${position}%`;

          // Highlight if threshold achieved
          milestone.element.classList.toggle(
            'achieved',
            total >= milestone.threshold
          );
        }
      });
    }

    updateMilestoneLabel(milestoneElement, amountInCents, labelText) {
      const labelElement = milestoneElement.querySelector(
        '.free-shipping--milestone-label'
      );
      if (!labelElement) {
        return;
      }

      const formattedPrice = this.formatPrice(amountInCents);

      // Update the label content
      labelElement.innerHTML = `
        <span>${labelText}</span>
        <strong>${formattedPrice}</strong>
      `;
    }

    updateTitleText(total, milestones) {
      const titleElement = this.querySelector('.free-shipping--title');
      if (!titleElement) {
        return;
      }

      // Find shipping and gift milestones
      const shippingMilestone = milestones.find(m => m.type === 'shipping');
      const giftMilestone = milestones.find(m => m.type === 'gift');

      const hasShipping = !!shippingMilestone;
      const hasGift = !!giftMilestone;
      const shippingAchieved =
        hasShipping && total >= shippingMilestone.threshold;
      const giftAchieved = hasGift && total >= giftMilestone.threshold;

      let titleHTML = '';

      // Both achieved
      if (shippingAchieved && giftAchieved) {
        titleHTML = '<strong>Free Shipping & Free Gift unlocked!</strong>';
      }
      // Only shipping achieved, gift pending
      else if (shippingAchieved && hasGift && !giftAchieved) {
        const remaining = giftMilestone.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `Free Shipping unlocked! Spend <strong>${formattedRemaining}</strong> more for Free Gift`;
      }
      // Only gift achieved, shipping pending (edge case if gift threshold is lower)
      else if (giftAchieved && hasShipping && !shippingAchieved) {
        const remaining = shippingMilestone.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `Free Gift unlocked! Spend <strong>${formattedRemaining}</strong> more for Free Shipping`;
      }
      // Only shipping achieved, no gift milestone
      else if (shippingAchieved && !hasGift) {
        titleHTML = '<strong>Free Shipping unlocked!</strong>';
      }
      // Only gift achieved, no shipping milestone
      else if (giftAchieved && !hasShipping) {
        titleHTML = '<strong>Free Gift unlocked!</strong>';
      }
      // Nothing achieved yet - show next milestone
      else {
        const nextMilestone = milestones[0];
        const remaining = nextMilestone.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        const milestoneType =
          nextMilestone.type === 'shipping' ? 'Free Shipping' : 'Free Gift';
        titleHTML = `<strong>Spend ${formattedRemaining} more for ${milestoneType}</strong>`;
      }

      titleElement.innerHTML = titleHTML;
    }

    formatPrice(amountInCents) {
      // Format the price using Shopify's formatMoney function
      let formattedPrice = '';
      if (
        typeof formatMoney === 'function' &&
        window.theme &&
        window.theme.settings
      ) {
        formattedPrice = formatMoney(
          amountInCents,
          window.theme.settings.money_with_currency_format
        );
      } else {
        // Fallback to basic formatting
        const symbol = Shopify.currency.active || '$';
        formattedPrice = symbol + Math.round(amountInCents / 100);
      }

      // Remove trailing zeros (.00, ,00, etc.)
      formattedPrice = formattedPrice.replace(/([.,])00$/, '');

      return formattedPrice;
    }

    async checkAndUpdateFreeGift(cart) {
      try {
        const freeGiftThreshold =
          parseInt(this.dataset.freeGiftThreshold, 10) || 0;
        const freeGiftVariantId = this.dataset.freeGiftVariantId;

        // Skip if free gift is disabled (threshold = 0 or no variant ID)
        if (freeGiftThreshold === 0 || !freeGiftVariantId) {
          return;
        }

        // Convert free gift threshold using currency rate (same as in update method)
        const convertedGiftThreshold = Math.round(
          freeGiftThreshold * (Shopify.currency.rate || 1)
        );

        const cartTotal = cart.total_price;

        // Count how many free gifts are in cart
        const freeGiftItems = cart.items.filter(
          item => item.variant_id.toString() === freeGiftVariantId
        );
        const freeGiftCount = freeGiftItems.length;
        const totalFreeGiftQty = freeGiftItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        // Add free gift if threshold met and none in cart
        if (cartTotal >= convertedGiftThreshold && freeGiftCount === 0) {
          await this.addFreeGift(freeGiftVariantId);
        }
        // Remove free gift(s) if threshold not met and any are in cart
        else if (cartTotal < convertedGiftThreshold && freeGiftCount > 0) {
          // Remove all instances of the free gift
          for (const item of freeGiftItems) {
            await this.removeFreeGift(cart, freeGiftVariantId);
          }
        }
        // Remove extra free gifts if more than 1 exist
        else if (freeGiftCount > 1 || totalFreeGiftQty > 1) {
          // Remove all but one
          for (let i = 1; i < freeGiftItems.length; i++) {
            await this.removeFreeGift(cart, freeGiftVariantId);
          }
        }
      } catch (error) {
        console.error('Error checking free gift:', error);
      }
    }

    async addFreeGift(variantId) {
      try {
        const formData = {
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _free_gift: 'true',
              },
            },
          ],
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          // Trigger UI refresh without waiting (fire and forget for speed)
          this.refreshCartUI();
        }
      } catch (error) {
        console.error('Error adding free gift:', error);
      }
    }

    async removeFreeGift(cart, variantId) {
      try {
        const freeGiftItem = cart.items.find(
          item => item.variant_id.toString() === variantId
        );

        if (!freeGiftItem) {
          return;
        }

        const lineNumber = cart.items.indexOf(freeGiftItem) + 1;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0,
          }),
        });

        if (response.ok) {
          // Trigger UI refresh without waiting (fire and forget for speed)
          this.refreshCartUI();
        }
      } catch (error) {
        console.error('Error removing free gift:', error);
      }
    }

    async refreshCartUI() {
      // The global processing flag prevents re-triggering, so we don't need to remove listeners

      // Trigger refreshes (they will fetch cart data themselves)
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh();
      }

      const cartPage = document.getElementById('Cart');
      if (cartPage) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Update cart count bubble optimistically (cart will be re-fetched by drawer/page)
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update progress bar immediately
        this.dataset.cartTotal = cart.total_price;
        this.update();

        // Update cart count
        const itemCount = cart.item_count;
        const bubbles = document.querySelectorAll('.thb-item-count');
        bubbles.forEach(bubble => {
          bubble.textContent = itemCount;
          bubble.classList.toggle('hide', itemCount === 0);
        });
      } catch (error) {
        console.error('Error updating cart UI:', error);
      }
    }
  }

  customElements.define('free-shipping', FreeShipping);
}
